'use client'

import * as React from 'react'
import { ArrowLeft, ArrowRight } from '@carbon/icons-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface ScrollRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Classes applied to the scrolling element itself (flex row, gaps, etc.) */
  className?: string
  /** Classes applied to the outer positioning wrapper */
  wrapperClassName?: string
  /** Optional ref to the scroller element (e.g. for scrollIntoView calls) */
  scrollerRef?: React.Ref<HTMLDivElement>
  children: React.ReactNode
}

/**
 * A horizontally scrollable row that never looks "cropped":
 * - soft gradient masks appear on whichever edge has more content, and
 * - subtle prev/next buttons appear while scrolling is possible.
 * Both are measurement-driven, so when the content fits (or the row becomes
 * a vertical/grid layout at a breakpoint) they disappear automatically.
 */
export function ScrollRow({
  className,
  wrapperClassName,
  scrollerRef,
  children,
  ...props
}: ScrollRowProps) {
  const innerRef = React.useRef<HTMLDivElement | null>(null)
  const [canLeft, setCanLeft] = React.useState(false)
  const [canRight, setCanRight] = React.useState(false)

  const setRefs = (el: HTMLDivElement | null) => {
    innerRef.current = el
    if (typeof scrollerRef === 'function') {
      scrollerRef(el)
    } else if (scrollerRef) {
      ;(scrollerRef as React.MutableRefObject<HTMLDivElement | null>).current =
        el
    }
  }

  const update = React.useCallback(() => {
    const el = innerRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setCanLeft(el.scrollLeft > 4)
    setCanRight(el.scrollLeft < max - 4)
  }, [])

  React.useEffect(() => {
    const el = innerRef.current
    if (!el) return
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    // Content width changes (fonts, responsive item sizes) also affect
    // scrollability, so observe the direct children too.
    Array.from(el.children).forEach((child) => ro.observe(child))
    window.addEventListener('resize', update)
    return () => {
      el.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      ro.disconnect()
    }
  }, [update])

  const nudge = (dir: 1 | -1) => {
    const el = innerRef.current
    if (!el) return
    el.scrollBy({
      left: dir * Math.max(el.clientWidth * 0.7, 160),
      behavior: 'smooth',
    })
  }

  // Fade the overflowing edge(s) with a mask instead of a hard crop. A mask
  // (rather than an overlay) works on any background colour.
  const mask =
    canLeft && canRight
      ? 'linear-gradient(to right, transparent 0, black 32px, black calc(100% - 44px), transparent 100%)'
      : canRight
        ? 'linear-gradient(to right, black 0, black calc(100% - 44px), transparent 100%)'
        : canLeft
          ? 'linear-gradient(to right, transparent 0, black 32px, black 100%)'
          : undefined

  return (
    <div className={cn('relative min-w-0', wrapperClassName)}>
      <div
        ref={setRefs}
        className={cn(
          'overflow-x-auto [&::-webkit-scrollbar]:hidden',
          className
        )}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitMaskImage: mask,
          maskImage: mask,
        }}
        {...props}
      >
        {children}
      </div>

      {canLeft && (
        <Button
          size="icon"
          aria-label="Scroll back"
          onClick={() => nudge(-1)}
          className="absolute left-0 top-1/2 z-10 -translate-y-1/2"
        >
          <ArrowLeft className="size-4" aria-hidden />
        </Button>
      )}
      {canRight && (
        <Button
          size="icon"
          aria-label="Scroll forward"
          onClick={() => nudge(1)}
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2"
        >
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      )}
    </div>
  )
}
